import { ApplicationFeedComment } from './ApplicationFeedComment';
import { ApplicationHistory } from './ApplicationHistory';
import { Email } from './Email';
import { Trigger } from './applicant_tracking/Trigger';
import { ApplicationRating } from './v1/applicant_tracking/ApplicationRating';

export enum ApplicationFeedType {
  // Generic
  Note = 'note',
  Applied = 'applied',
  StageMove = 'stage_move',
  ProfileUpdate = 'profile_update',
  Rejection = 'rejection',
  Unrejection = 'unrejection',
  ScorecardFill = 'scorecard_fill',
  ScorecardEdit = 'scorecard_edit',
  Details = 'details',
  ApplicationRatingAdded = 'application_rating_added',
  ApplicationRatingUpdated = 'application_rating_updated',
  ReferralCreated = 'referral_created',
  // Offer
  Offer = 'offer',
  OfferRejected = 'offer_rejected',
  OfferAccepted = 'offer_accepted',
  OfferRevoked = 'offer_revoked',
  SignableOffer = 'signable_offer',
  SignableOfferAccepted = 'signable_offer_accepted',
  SignableOfferRejected = 'signable_offer_rejected',
  SignableOfferRevoked = 'signable_offer_revoked',
  SignableOfferExpired = 'signable_offer_expired',
  OfferRequisitionCreated = 'offer_requisition_created',
  OfferRequisitionUpdated = 'offer_requisition_updated',
  OfferRequisitionApproved = 'offer_requisition_approved',
  OfferRequisitionDeclined = 'offer_requisition_declined',
  // HRIS
  PushedToHris = 'pushed_to_hris',
  // Interview Scheduling
  InterviewSchedule = 'interview_schedule',
  InterviewScheduleByCandidate = 'interview_schedule_by_candidate',
  InterviewReschedule = 'interview_reschedule',
  InterviewCancel = 'interview_cancel',
  InterviewComplete = 'interview_complete',
  InterviewAutomatedSelfSchedule = 'interview_automated_self_schedule',
  InterviewSelfSchedule = 'interview_self_schedule',
  // Pulse
  PulseSurveyShare = 'pulse_survey_share',
  PulseSurveySent = 'pulse_survey_sent',
  PulseSurveyCompleted = 'pulse_survey_completed',
  // Success
  RcSurveyShared = 'rc_survey_shared',
  RcSurveySent = 'rc_survey_sent',
  RcSurveyCompleted = 'rc_survey_completed',
  RcSelfAssessmentCompleted = 'rc_self_assessment_completed',
  // Background Check
  BackgroundCheckInitiated = 'background_check_initiated',
  BackgroundCheckCompleted = 'background_check_completed',
  BackgroundCheckCancelled = 'background_check_cancelled',
  // Email
  EmailSent = 'email_sent',
  EmailReceived = 'email_received',
  // Triggers
  TriggerEmailScheduled = 'trigger_email_scheduled',
  TriggerEmailCancelled = 'trigger_email_cancelled',
  TriggerEmailSent = 'trigger_email_sent',
  TriggerRejection = 'trigger_rejection',
}

export interface ApplicationFeed {
  id: number;
  applicationId?: number;
  entryType: ApplicationFeedType;
  authorId?: number;
  authorName?: string;
  jobName?: string;
  createdAt: Date;
  comments: ApplicationFeedComment[];
  editedAt?: Date;
  deletedAt?: Date;
}

export interface ApplicationFeedInterview {
  id: number;
  interviewStage: { id: number; name: string };
}

export interface ApplicationFeedScorecard {
  id: number;
  averageRating: number;
  interview?: ApplicationFeedInterview;
}

export interface ApplicationFeedEmail extends ApplicationFeed {
  email: Email;
}

export interface History extends ApplicationFeed {
  applicationHistory: ApplicationHistory;
}

export interface Interview extends ApplicationFeed {
  interview: ApplicationFeedInterview;
}

export interface Note extends ApplicationFeed {
  note: string;
}

export interface OfferFeed extends ApplicationFeed {
  authorEmail: string;
  email: Email;
  note: string;
  revokeOfferPermitted: boolean;
  offerId?: number;
  offerRejectReasons?: string[];
}

export interface SignableOfferFeed extends ApplicationFeed {
  authorEmail: string;
  email: Email;
  signableOfferId?: number;
  signableOfferStatus?: string;
  signableOfferExpiresAt?: Date;
  signableOfferDeclineReason?: string;
  signableOfferDeclinedBy?: string;
  revokeSignableOfferPermitted?: boolean;
}

export interface OfferRequisitionFeed extends ApplicationFeed {
  offerRequisitionId: number;
}

export interface ScorecardFill extends ApplicationFeed {
  scorecard: ApplicationFeedScorecard;
}

export interface ApplicationRatingFeed extends ApplicationFeed {
  applicationRating: ApplicationRating;
}

export type Simple = ApplicationFeed;

export interface PulseSurveyActions extends ApplicationFeed {
  ceSurveyName: string;
}

export type TriggerExecutionStatus = 'scheduled' | 'cancelled' | 'executed';
export type TriggerExecutionCancelReason =
  | 'move_to_stage'
  | 'auto_reject'
  | 'feed_action'
  | 'rejection';
export interface TriggerExecution {
  status: TriggerExecutionStatus;
  id: number;
  scheduledExecuteAt: Date;
  cancelledByName?: string;
  cancelReason?: TriggerExecutionCancelReason;
}

export interface TriggerActions extends ApplicationFeed {
  trigger: Trigger;
  triggerExecution: TriggerExecution;
}
